import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';

export interface ResourceState {
  dimensionPower: number;
  dimensionFragments: number;
  transcendencePoints: number;
  worldSpecificResources: Record<string, number>;
}

const initialState: ResourceState = {
  dimensionPower: 0,
  dimensionFragments: 0,
  transcendencePoints: 0,
  worldSpecificResources: {
    // 最初の世界のリソース
    magic: 0,
  },
};

export const resourceSlice: Slice<ResourceState> = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    gainDimensionPower: (state, action: PayloadAction<number>) => {
      state.dimensionPower += action.payload;
    },
    spendDimensionPower: (state, action: PayloadAction<number>) => {
      state.dimensionPower = Math.max(0, state.dimensionPower - action.payload);
    },
    gainDimensionFragments: (state, action: PayloadAction<number>) => {
      state.dimensionFragments += action.payload;
    },
    spendDimensionFragments: (state, action: PayloadAction<number>) => {
      state.dimensionFragments = Math.max(0, state.dimensionFragments - action.payload);
    },
    gainTranscendencePoints: (state, action: PayloadAction<number>) => {
      state.transcendencePoints += action.payload;
    },
    spendTranscendencePoints: (state, action: PayloadAction<number>) => {
      state.transcendencePoints = Math.max(0, state.transcendencePoints - action.payload);
    },
    gainWorldResource: (state, action: PayloadAction<{ resourceId: string; amount: number }>) => {
      const { resourceId, amount } = action.payload;
      state.worldSpecificResources[resourceId] = (state.worldSpecificResources[resourceId] || 0) + amount;
    },
    spendWorldResource: (state, action: PayloadAction<{ resourceId: string; amount: number }>) => {
      const { resourceId, amount } = action.payload;
      const currentAmount = state.worldSpecificResources[resourceId] || 0;
      state.worldSpecificResources[resourceId] = Math.max(0, currentAmount - amount);
    },
    addWorldResource: (state, action: PayloadAction<string>) => {
      const resourceId = action.payload;
      if (!state.worldSpecificResources[resourceId]) {
        state.worldSpecificResources[resourceId] = 0;
      }
    },
    // 転生時のリソースリセット (一部は保持する可能性あり)
    resetOnTranscendence: (state, action: PayloadAction<{ keepResources?: string[] }>) => {
      const { keepResources = [] } = action.payload;
      
      // 次元パワーをリセット
      state.dimensionPower = 0;
      
      // 保持する世界リソースを除外してリセット
      const newWorldResources: Record<string, number> = {};
      keepResources.forEach(resourceId => {
        if (state.worldSpecificResources[resourceId]) {
          newWorldResources[resourceId] = state.worldSpecificResources[resourceId];
        }
      });
      
      state.worldSpecificResources = newWorldResources;
    },
  },
});

export const {
  gainDimensionPower,
  spendDimensionPower,
  gainDimensionFragments,
  spendDimensionFragments,
  gainTranscendencePoints,
  spendTranscendencePoints,
  gainWorldResource,
  spendWorldResource,
  addWorldResource,
  resetOnTranscendence
} = resourceSlice.actions;

export const resourceReducer = resourceSlice.reducer; 